import {Cube, CubeCell, CubeCellValue, CubeMeasure, CubeMember, MEASURES_CHANNEL} from '../types';

/** One position on a channel: a dimension member or (for `$measures`) a measure. */
export interface AxisItem {
    id: string;
    label: string;
    index: number;
    member?: CubeMember;
    measure?: CubeMeasure;
}

export default class CubeIndex {

    private readonly cells = new Map<string, CubeCell>();

    constructor(public readonly cube: Cube) {
        cube.cells.forEach(cell => this.cells.set(cell.coords.join(','), cell));
    }

    public dimensionIndex(id: string): number {
        return this.cube.dimensions.findIndex(dimension => dimension.id === id);
    }

    public measure(id: string): CubeMeasure {
        return this.cube.measures.find(measure => measure.id === id) ?? this.cube.measures[0];
    }

    public items(channel?: string): AxisItem[] {
        if (!channel) {
            return [];
        }
        if (channel === MEASURES_CHANNEL) {
            return this.cube.measures.map((measure, index) => ({id: measure.id, label: measure.label, index, measure}));
        }
        const dimension = this.cube.dimensions[this.dimensionIndex(channel)];
        return (dimension?.members ?? []).map((member, index) => ({id: member.key, label: member.label, index, member}));
    }

    /** Cell at given member index per dimension id; dimensions not mentioned stay on their first member. */
    public cell(positions: Record<string, number>): CubeCell | undefined {
        const coords = this.cube.dimensions.map(dimension => positions[dimension.id] ?? 0);
        return this.cells.get(coords.join(','));
    }

    public value(positions: Record<string, number>, measureId: string): CubeCellValue | undefined {
        return this.cell(positions)?.values[measureId];
    }

    /** True when the cell is a total over a dimension that is not in `usedPositions` (a rollup, not a real combination). */
    public isOutsideTotal(cell: CubeCell, usedPositions: number[]): boolean {
        return this.cube.dimensions.some((dimension, position) =>
            !usedPositions.includes(position) && dimension.members[cell.coords[position]]?.role === 'total');
    }

    public isEmpty(): boolean {
        return this.cube.cells.length === 0;
    }
}
